export const getCookieValue = (cookieKey: string): string | undefined => {
  if (typeof document === "undefined") {
    return undefined;
  }
  const existingCookie = document.cookie
    .split("; ")
    .find(row => row.startsWith(`${cookieKey}=`))
    ?.split("=")[1];
  return existingCookie;
};

export type SplitCookiePayload = {
  treatment: string;
  label: string; // Always a string, defaults to "default rule" in edge functions
};

export const getSplitCookieValue = (
  cookieKey: string,
): SplitCookiePayload | undefined => {
  if (typeof document === "undefined") {
    return undefined;
  }
  const raw = document.cookie
    .split("; ")
    .find(row => row.startsWith(`${cookieKey}=`))
    ?.split("=")[1];
  if (!raw) return undefined;
  try {
    const decoded = decodeURIComponent(raw);
    const parsed: unknown = JSON.parse(decoded);
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      "treatment" in parsed
    ) {
      const obj = parsed as { treatment?: unknown; label?: unknown };
      return {
        treatment: String(obj.treatment ?? "control"),
        label: String(obj.label ?? "default rule"),
      };
    }
  } catch {
    return undefined;
  }
  return undefined;
};

/**
 * setCookieValue - sets a cookie in the browser's document object
 * @param {string} cookieKey The cookie's key or name.
 * @param {string} cookieValue The cookie's value
 * @param {string|number} [expiration] Optional expiration date (UTC string) or maxAge in seconds.
 * @param {string} [domain] Optional domain, can be set to empty string to use the local domain
 */

export const setCookieValue = (
  cookieKey: string,
  cookieValue: string,
  expiration?: string | number,
  domain?: string,
): void => {
  if (typeof document === "undefined") {
    return;
  }

  const getDomain = (hostname: string): string => {
    if (domain) return `Domain=${domain};`;
    if (hostname.includes("cloudfront.net")) return `Domain=${hostname};`;
    return `Domain=${process.env.GATSBY_HH_DOMAIN ?? ".mweb.hingehealth.dev"};`;
  };

  const localDomain = getDomain(window.location.hostname);

  if (expiration !== undefined) {
    if (typeof expiration === "number") {
      document.cookie = `${cookieKey}=${cookieValue};Path=/;${localDomain}max-age=${expiration};`;
    } else {
      document.cookie = `${cookieKey}=${cookieValue};Path=/;${localDomain}expires=${expiration};`;
    }
  } else {
    document.cookie = `${cookieKey}=${cookieValue};Path=/;${localDomain}`;
  }
};
